import { Box, Typography, useTheme } from '@mui/material';
import { IconChevronLeft } from '@tabler/icons-react';

/**
 * v4 page hero (DESIGN_SYSTEM.md section 7.2, #1377 board). List pages carry a
 * plain-case 22px medium title on the left (optionally preceded by a back
 * link) and a right-aligned auxiliary block (payday, totals) on the same
 * baseline row. Bottom hairline divides it from the page content.
 *
 * `value` / `context` remain for pages that show a figure under the title
 * (transitional; detail pages build their own single-line heroes). The hero
 * value color stays caller-controlled (`valueColor`) because its meaning is
 * page-specific. Defaults to text.primary when unset.
 *
 * @param {object} props
 * @param {React.ReactNode} props.label       page title (plain case, e.g. "Ledgers")
 * @param {{label: React.ReactNode, onClick: function}} [props.back]
 *                                             back link INLINE-LEFT of the title
 *                                             (kit BrdHero single row, #1401)
 * @param {React.ReactNode} [props.value]     figure under the title (string or node)
 * @param {string} [props.valueColor]         figure color (a theme color string)
 * @param {React.ReactNode} [props.context]   dim context line below the figure
 * @param {React.ReactNode} [props.aux]       right-aligned auxiliary block
 * @param {object} [props.sx]                 extra sx merged onto the hero
 */
const PageHero = ({ label, back, value, valueColor, context, aux, sx, ...rest }) => {
  const theme = useTheme();
  const { ds, palette } = theme;

  return (
    <Box
      component="header"
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '13px 0 12px',
        borderBottom: `1px solid ${palette.divider}`,
        ...sx,
      }}
      {...rest}
    >
      {/* flex 1 so full-width children (the Budget Detail progress bar) span
          the hero instead of shrinking to the text width (#1377 review fix). */}
      <Box sx={{ flex: '1 1 auto', minWidth: 0 }}>
        {/* Kit BrdHero (#1401): back link and title share ONE row, back on
            the left. The chevron hangs into the gutter (-19px = 15px glyph +
            4px gap) so the back label text-aligns flush with the content. */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: '11px', md: '14px' } }}>
          {back != null && (
            <Box
              component="button"
              type="button"
              onClick={back.onClick}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                background: 'none',
                border: 0,
                padding: 0,
                // xs gutter is 16px, too narrow for the full 19px hang.
                marginLeft: { xs: '-4px', sm: '-19px' },
                flexShrink: 0,
                cursor: 'pointer',
                color: palette.interactive,
                fontSize: '13px',
                fontFamily: 'inherit',
              }}
            >
              <IconChevronLeft size={15} aria-hidden />
              {back.label}
            </Box>
          )}
          {label != null && (
            <Typography
              component="h1"
              sx={{
                fontSize: { xs: '20px', md: `${ds.font.display}px` },
                fontWeight: ds.font.weightMedium,
                color: palette.text.primary,
                lineHeight: 1.2,
                margin: 0,
                minWidth: 0,
              }}
            >
              {label}
            </Typography>
          )}
        </Box>
        {value != null && (
          <Typography
            component="div"
            sx={{
              fontSize: `${ds.font.hero}px`,
              fontWeight: ds.font.weightMedium,
              letterSpacing: ds.font.letterHero,
              fontVariantNumeric: 'tabular-nums',
              color: valueColor || palette.text.primary,
              marginTop: '2px',
            }}
          >
            {value}
          </Typography>
        )}
        {context != null && (
          <Typography
            component="div"
            sx={{ fontSize: `${ds.font.meta}px`, color: palette.text.dim, marginTop: '2px' }}
          >
            {context}
          </Typography>
        )}
      </Box>
      {aux != null && (
        <Box sx={{ textAlign: 'right', flexShrink: 0, paddingLeft: `${ds.space[6]}px` }}>
          {aux}
        </Box>
      )}
    </Box>
  );
};

export default PageHero;
